
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Phone, Mail, LogOut, Edit, Check, X } from 'lucide-react';
import { useOnboardingContext } from '@/context/OnboardingContext';
import PageLayout from '@/components/PageLayout';
import PhoneInput from '@/components/PhoneInput';
import { validatePhoneNumber } from '@/utils/phone-validator';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

const Profile = () => {
  const navigate = useNavigate();
  const { userData, resetOnboarding, saveUserData } = useOnboardingContext() || { userData: null };
  const { t } = useTranslation();
  
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(userData?.phoneNumber || '');
  const [phoneError, setPhoneError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = () => {
    if (resetOnboarding) {
      resetOnboarding();
      navigate('/onboarding');
    }
  };

  const handleEditPhone = () => {
    setPhoneNumber(userData?.phoneNumber || '');
    setPhoneError('');
    setIsEditingPhone(true);
  };

  const handleCancelEdit = () => {
    setIsEditingPhone(false);
    setPhoneError('');
  };

  const handleSavePhone = async () => {
    // Validate phone number
    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError(t('profile.phoneValidation'));
      return;
    }

    setIsLoading(true);
    try {
      // Check if phone number already exists for another user
      const { data: existingUsers, error: checkError } = await supabase
        .from('users')
        .select('id')
        .eq('phone_number', phoneNumber)
        .neq('email', userData?.email || '')
        .limit(1);
      
      if (checkError) {
        console.error("Error checking phone number:", checkError);
        throw new Error(t('common.error'));
      }
      
      if (existingUsers && existingUsers.length > 0) {
        setPhoneError(t('profile.phoneInUse'));
        setIsLoading(false);
        return;
      }

      // Update phone number in database
      const { error: updateError } = await supabase
        .from('users')
        .update({ phone_number: phoneNumber })
        .eq('email', userData?.email || '');
      
      if (updateError) {
        console.error("Error updating phone number:", updateError);
        throw new Error(t('common.error'));
      }

      // Update localStorage
      if (userData && saveUserData) {
        const updatedUserData = {
          ...userData,
          phoneNumber
        };
        saveUserData(updatedUserData);
      }

      console.log('Numéro de téléphone mis à jour avec succès');
      alert('Numéro de téléphone mis à jour avec succès');
      
      setIsEditingPhone(false);
    } catch (error) {
      console.error("Error saving phone number:", error);
      alert(error instanceof Error ? error.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  // Get initials for avatar
  const getInitials = () => {
    if (!userData?.name) return 'U';
    return userData.name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <PageLayout 
      title={t('profile.title')} 
      colorScheme="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600"
    >
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center mb-6">
          <Avatar className="h-24 w-24">
            <AvatarFallback className="text-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </div>
        
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="mr-2 h-5 w-5" />
              {t('profile.personalInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('profile.fullName')}</p>
              <p className="font-medium">{userData?.name || t('common.loading')}</p>
            </div>
            
            {isEditingPhone ? (
              <div className="space-y-2">
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  error={phoneError}
                  label={t('profile.phoneNumber')}
                  placeholder={t('profile.enterNumber')}
                />
                <div className="flex space-x-2 mt-2">
                  <Button 
                    size="sm" 
                    onClick={handleSavePhone} 
                    disabled={isLoading}
                    className="flex-1"
                  >
                    <Check className="h-4 w-4 mr-1" /> {t('common.save')}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    <X className="h-4 w-4 mr-1" /> {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <p>{userData?.phoneNumber || t('profile.numberNotDefined')}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={handleEditPhone}
                  className="p-1 h-auto"
                >
                  <Edit className="h-4 w-4" />
                  <span className="sr-only">{t('profile.editPhone')}</span>
                </Button>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <p>{userData?.email || t('common.loading')}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('profile.accountSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              variant="destructive" 
              className="w-full" 
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('profile.logout')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Profile;
